"use client";
import React, { useState, useEffect } from "react";
import { useBilling } from "../../../../../context/BillingContext";
import { toast } from "react-toastify";
import { useRazorpay } from "../../../../../context/RazorpayContext";

const UpgradeModal = ({ show, onClose, openModal }) => {
  const { plans, currentPlan: contextCurrentPlan } = useBilling();
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [upgradeLoader, setUpgradeLoader] = useState(false);
  const { handlePreviewPlan } = useRazorpay();
  const [expandedPlans, setExpandedPlans] = useState({});
  const [billingCycle, setBillingCycle] = useState("monthly");

  // Use currentPlan from context or find it in plans
  const currentPlan =
    contextCurrentPlan ||
    plans?.find((plan) => plan.current || plan.current_active);

  useEffect(() => {
    // Robust check for subscription interval to initialize billing cycle
    const interval =
      currentPlan?.subscription?.interval ||
      currentPlan?.Data?.subscription?.interval ||
      currentPlan?.data?.subscription?.interval;

    if (interval) {
      setBillingCycle(interval === "yearly" ? "yearly" : "monthly");
    }
  }, [currentPlan]);

  if (!show) return null;

  const isActuallyCurrentCycle =
    billingCycle === currentPlan?.subscription?.interval;

  // A plan is the "Current Plan" only if its ID matches AND the cycle matches
  const isPlanCurrent = (plan) =>
    plan.id === currentPlan?.id && isActuallyCurrentCycle;

  // Filter other plans: everything except the one that is currently active in the current cycle but also remove free plan
  const isUserOnPaidPlan = !currentPlan?.is_free;

  const otherPlans = plans?.filter((plan) => {
    // remove current plan
    if (isPlanCurrent(plan)) return false;

    // hide free plan if user already has paid plan
    if (isUserOnPaidPlan && plan?.is_free) return false;

    return true;
  });

  const closeModal = () => {
    setSelectedPlanId(null);
    onClose();
  };

  const parsePrice = (price) => {
    if (!price || price === "Free") return 0;
    return parseFloat(price.toString().replace(/[₹,\s]/g, ""));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedPlan = plans?.find((plan) => plan.id === selectedPlanId);

      if (!selectedPlan || !currentPlan) {
        toast.error("Unable to determine selected or current plan details");
        return;
      }

      const selectedPriceString =
        billingCycle === "yearly"
          ? selectedPlan.prices?.yearly
          : selectedPlan.prices?.monthly;
      const currentPriceString =
        billingCycle === "yearly"
          ? currentPlan.prices?.yearly
          : currentPlan.prices?.monthly;

      const selectedPrice = parsePrice(selectedPriceString);
      const currentPrice = parsePrice(currentPriceString);

      const isDowngrade = selectedPrice < currentPrice;

      if (isDowngrade) {
        openModal("downgrade", { selectedPlanId, billingCycle });
      } else {
        setUpgradeLoader(true);
        await handlePreviewPlan(
          e,
          selectedPlanId,
          "upgrade",
          null,
          false,
          billingCycle,
        );
        setSelectedPlanId(null);
        closeModal();
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to upgrade the plan";
      toast.error(errorMessage, { position: "bottom-right", autoClose: 5000 });
    } finally {
      setUpgradeLoader(false);
    }
  };

  const normalizedPoints = (descriptionPoints) => {
    return Array.isArray(descriptionPoints)
      ? descriptionPoints.flatMap((item) => item.split("\n"))
      : descriptionPoints?.split("\n") || [];
  };

  const toggleReadMore = (key) => {
    setExpandedPlans((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderPlanCard = (plan, isCurrent = false) => {

    if (plan?.id !== currentPlan?.id && plan?.is_free) {
      return null;
    }
    const isYearly = billingCycle === "yearly";

    const strikePrice = isYearly
      ? plan?.prices?.base_yearly
      : plan?.prices?.base_monthly;

    const savePrice = isYearly
      ? plan?.prices?.yearly_save
      : plan?.prices?.monthly_save;

    const formattedDescription = plan?.description
      ?.replace(/\\n/g, "\n")
      ?.replace(/\\u0026/g, "&")
      ?.replace(/\\u003c/g, "<")
      ?.replace(/\\u003e/g, ">");

    const points = normalizedPoints(formattedDescription || "");
    const price =
      billingCycle === "yearly" ? plan?.prices?.yearly : plan?.prices?.monthly;
    const isSelected = selectedPlanId === plan.id;

    if (plan?.is_free && billingCycle !== "monthly") return;

    return (
      <div key={plan.id} className="mb-3">
        <div
          className={`plan-card-v2 ${isCurrent ? "current-plan disabled-plan" : ""} ${isSelected ? "active-plan" : ""}`}
          onClick={() => !isCurrent && setSelectedPlanId(plan.id)}
        >
          <div className="plan-card-header">
            <div className="plan-price-section">
              {currentPlan?.is_free && parsePrice(price) === 0 ? (
                <span className="price-value">
                  {currentPlan?.trial?.days} Days Free Trial
                </span>
              ) : (
                <div className="modern-plan-price">
                  <div className="d-flex align-items-end gap-1 flex-wrap">
                    <span className="price-value">{price}</span>

                    <span className="price-period">
                      /{billingCycle === "yearly" ? "yr" : "mo"}
                    </span>
                  </div>

                  {/* Strike Price + Save Text */}
                  {/* Strike Price + Save */}
                  {!plan?.trial && strikePrice && savePrice && (
                    <div className="price-save-row">
                      <span className="plan-strike-price">{strikePrice}</span>

                      <span className="plan-save-text">Save {savePrice}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="plan-selection-section">
              {isCurrent ? (
                <span className="current-plan-badge">Current Plan</span>
              ) : (
                <div className="plan-radio-circle">
                  {isSelected && <div className="radio-dot" />}
                </div>
              )}
            </div>
          </div>

          <div className="plan-content">
            <h5 className="plan-title text-capitalize">{plan.name}</h5>
            <p className="plan-subtitle">{points[0]}</p>

            {expandedPlans[plan.id || "current"] && (
              <ul className="plan-features-list">
                {points.slice(1).map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>
            )}

            {points.length > 1 && (
              <span
                className="read-more-link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleReadMore(plan.id || "current");
                }}
              >
                {expandedPlans[plan.id || "current"]
                  ? "Read less"
                  : "Read more"}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="modal d-flex upgrade-modal-v2"
      style={{
        display: "flex",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1052,
      }}
      onClick={closeModal}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0">
          <div className="modal-header border-0 pb-0 justify-content-center position-relative">
            <div className="text-center mt-3">
              <h4 className="fw-bold mb-1">Choose Your New Plan</h4>
              <p className="text-muted mb-0">
                Select the plan that best fits your needs
              </p>
            </div>
            <button
              type="button"
              className="btn-close position-absolute end-0 top-0 m-3"
              onClick={closeModal}
            ></button>
          </div>

          <div className="modal-body package-modal px-4 pt-4">
            <div className="billing-cycle-toggle-wrapper mb-4">
              <div className="billing-cycle-toggle">
                <button
                  className={`cycle-btn ${billingCycle === "monthly" ? "active" : ""}`}
                  onClick={() => setBillingCycle("monthly")}
                >
                  Monthly
                </button>
                <button
                  className={`cycle-btn ${billingCycle === "yearly" ? "active" : ""}`}
                  onClick={() => setBillingCycle("yearly")}
                >
                  Annual
                </button>
              </div>
            </div>

            {/* Show the current plan at the top only if it's the current cycle */}
            {currentPlan &&
              isActuallyCurrentCycle &&
              renderPlanCard(currentPlan, true)}

            {otherPlans?.length > 0
              ? otherPlans.map((plan) =>
                  renderPlanCard(plan, isPlanCurrent(plan)),
                )
              : !currentPlan &&
                !otherPlans?.length && (
                  <div className="text-center py-4">
                    <p>No plans available at the moment.</p>
                  </div>
                )}

            {otherPlans?.length === 0 && currentPlan && (
              <div className="highest-plan-banner mb-3">
                <span>
                  <i className="bi bi-rocket-takeoff"></i>
                </span>
                <span>You’re already enjoying our top-tier plan</span>
              </div>
            )}
          </div>

          <div className="modal-footer border-0 px-4 pb-4">
            <button
              className="btn btn-outline-secondary flex-grow-1"
              disabled={upgradeLoader}
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary flex-grow-1 continue-btn"
              disabled={!selectedPlanId || upgradeLoader}
              onClick={handleSubmit}
            >
              {upgradeLoader ? "Processing..." : "Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
